import { DT, FPS, EMPTY_INPUT, MOVES, STAGES, fighterDefinition } from './data.js';
import { clamp, approach, circleOverlap, radians } from './math.js';
import { hurtboxes, landingPlatform, outsideBlastZone } from './collision.js';
import { validateConfig } from './config.js';
const state = (f, next) => { if (f.state !== next) { f.state = next; f.stateFrame = 0; } };
const sign = n => n < 0 ? -1 : 1;
const createFighter = (id, definition, stocks) => ({
  id, definition, x: id === 0 ? -4 : 4, y: 0, prevX: id === 0 ? -4 : 4, prevY: 0,
  vx: 0, vy: 0, facing: id === 0 ? 1 : -1, damage: 0, stocks, grounded: true,
  platformId: 'base', jumpsLeft: 1, state: 'idle', stateFrame: 0,
  attack: null, attackFrame: 0, attackSerial: 0, hitTargets: [], charging: false, chargeFrames: 0,
  hitstop: 0, stun: 0, invuln: 0, shield: 100, shieldStun: 0,
  dodge: 0, dodgeCooldown: 0, airDodgeUsed: false, landing: 0, jumpSquat: 0,
  dropFrames: 0, ledgeCooldown: 0, ledgeSide: 0, jumpBuffer: 0, queuedAttack: null,
  recoveryUsed: false, helpless: false, fastFall: false, ko: 0,
});
export function hitboxes(f) {
  if (!f.attack || f.charging || f.ko || f.stocks === 0) return [];
  const m = MOVES[f.attack];
  if (f.attackFrame < m.startup || f.attackFrame >= m.startup + m.active) return [];
  return [{ x: f.x + m.x * f.facing, y: f.y + m.y, r: m.r }];
}
export function knockback(move, percent, weight, charge = 0, power = 1) {
  const multiplier = 1 + clamp(charge, 0, 45) / 45 * 0.6;
  return (move.base + percent * move.growth + move.damage * 0.12) * Math.sqrt(100 / weight) * multiplier * power;
}
/** Pure 60 Hz combat simulation, independent of Three.js and the DOM.
 * Original ruleset, not a Melee port or frame-accurate emulator.
 */
export class Engine {
  constructor(settings) { this.reset(settings); }
  reset(settings = this.settings) {
    this.settings = validateConfig(settings); this.stage = STAGES[this.settings.stage];
    this.fighters = [0, 1].map(id => createFighter(id, fighterDefinition(this.settings, id), this.settings.stocks));
    if (this.settings.mode === 'training') this.fighters[1].damage = this.settings.trainingDamage;
    this.phase = 'ready'; this.frame = 0; this.countdown = 150;
    this.remaining = this.settings.minutes * FPS * 60;
    this.winner = null; this.events = []; this.totalHits = 0;
  }
  start() { this.reset(); this.phase = 'countdown'; }
  trainingReset(percent = this.settings.trainingDamage) {
    if (this.settings.mode !== 'training') return;
    this.settings.trainingDamage = clamp(Number(percent) || 0, 0, 300);
    this.fighters = [0, 1].map(id => createFighter(id, fighterDefinition(this.settings, id), this.settings.stocks));
    this.fighters[1].damage = this.settings.trainingDamage;
  }
  snapshot() { return structuredClone({ phase: this.phase, frame: this.frame, remaining: this.remaining, winner: this.winner, fighters: this.fighters, totalHits: this.totalHits }); }
  emit(type, f, extra = {}) { this.events.push({ type, x: f.x, y: f.y + 0.9, player: f.id, color: f.definition.color, ...extra }); }
  beginAttack(f, id) {
    if (id === 'recovery' && f.recoveryUsed) return false;
    f.attack = id; f.attackFrame = 0; f.attackSerial++; f.hitTargets = [];
    f.chargeFrames = 0; f.charging = Boolean(MOVES[id].chargeable);
    f.invuln = 0;
    if (id === 'recovery') {
      // Clear the floor during startup; otherwise a grounded special immediately
      // lands again and is canceled before reaching its launch frame.
      const fromGround = f.grounded;
      f.recoveryUsed = true; f.fastFall = false;
      f.vy = fromGround ? 4 : 0;
      if (fromGround) f.y += 0.05;
      f.grounded = false; f.platformId = null;
    }
    state(f, f.charging ? 'charge' : 'attack'); return true;
  }
  chooseAttack(f, action, control) {
    if (action === 'special') return 'recovery';
    if (action === 'heavy' && f.grounded) return control.y > 0.45 ? 'upsmash' : control.y < -0.45 ? 'downsmash' : 'smash';
    if (!f.grounded) return control.y > 0.45 ? 'uair' : control.y < -0.45 ? 'dair' : Math.abs(control.x) < 0.3 ? 'nair' : control.x * f.facing < 0 ? 'bair' : 'fair';
    return control.y > 0.45 ? 'upper' : control.y < -0.45 ? 'sweep' : Math.abs(control.x) > 0.3 ? 'tilt' : 'jab';
  }
  step(controls = [EMPTY_INPUT, EMPTY_INPUT]) {
    this.events = [];
    if (this.phase === 'ready' || this.phase === 'over') return;
    this.frame++;
    if (this.phase === 'countdown') {
      this.countdown--;
      if (this.countdown === 0) { this.phase = 'playing'; this.emit('go', this.fighters[0]); }
      return;
    }
    if (this.settings.mode !== 'training') this.remaining--;
    for (const f of this.fighters) { f.prevX = f.x; f.prevY = f.y; }
    for (const f of this.fighters) this.advance(f, controls[f.id] || EMPTY_INPUT);
    this.resolveHits(controls);
    for (const f of this.fighters) if (!f.ko && f.stocks > 0 && outsideBlastZone(f, this.stage)) this.ringOut(f);
    if (this.settings.mode !== 'training') {
      const living = this.fighters.filter(f => f.stocks > 0);
      if (living.length < 2) { this.phase = 'over'; this.winner = living.length ? living[0].id : -1; }
      else if (this.remaining <= 0) {
        this.phase = 'over'; this.remaining = 0;
        const [a, b] = this.fighters;
        this.winner = a.stocks !== b.stocks ? (a.stocks > b.stocks ? 0 : 1) : a.damage !== b.damage ? (a.damage < b.damage ? 0 : 1) : -1;
      }
    }
  }
  advance(f, c) {
    if (f.stocks === 0) return;
    if (f.ko > 0) { if (--f.ko === 0) this.respawn(f); return; }
    if (c.jump) f.jumpBuffer = 8;
    if (c.special || c.heavy || c.attack) f.queuedAttack = { type: c.special ? 'special' : c.heavy ? 'heavy' : 'attack', ttl: 7, x: c.x, y: c.y };
    if (f.hitstop > 0) { f.hitstop--; return; }
    f.stateFrame++;
    for (const key of ['invuln', 'dropFrames', 'ledgeCooldown', 'dodgeCooldown']) if (f[key] > 0) f[key]--;
    if (f.jumpBuffer > 0) f.jumpBuffer--;
    if (f.queuedAttack && --f.queuedAttack.ttl <= 0) f.queuedAttack = null;
    if (f.state === 'ledge') { this.handleLedge(f, c); return; }
    let locked = false;
    if (f.stun > 0) {
      f.stun--; locked = true; state(f, 'hurt'); f.vx += c.x * 0.028;
    } else if (f.shieldStun > 0) { f.shieldStun--; locked = true; state(f, 'shield'); }
    else if (f.dodge > 0) { f.dodge--; locked = true; state(f, 'dodge'); if (!f.dodge) f.vx *= 0.55; }
    else if (f.landing > 0) { f.landing--; locked = true; state(f, 'landing'); }
    else if (f.jumpSquat > 0) {
      locked = true;
      if (--f.jumpSquat === 0) { f.vy = f.definition.jump * (c.jumpHeld ? 1 : 0.65); f.grounded = false; f.fastFall = false; this.emit('jump', f); }
    }
    if (f.attack) {
      locked = true; const m = MOVES[f.attack];
      if (f.charging) {
        state(f, 'charge'); f.chargeFrames++;
        if (!c.heavyHeld || f.chargeFrames >= 45) { f.charging = false; f.attackFrame = 0; state(f, 'attack'); }
      } else {
        f.attackFrame++; state(f, 'attack');
        if (m.recoveryMove && f.attackFrame === m.startup) { f.vy = 19.5; f.vx = c.x * 7; this.emit('recovery', f); }
        if (f.attackFrame >= m.startup + m.active + m.recovery) {
          if (m.recoveryMove) f.helpless = true;
          f.attack = null; f.charging = false; locked = false;
        }
      }
    }
    if (!locked && !f.helpless) {
      if (f.grounded && Math.abs(c.x) > 0.2) f.facing = sign(c.x);
      if (c.dodge && f.dodgeCooldown === 0 && (f.grounded || !f.airDodgeUsed)) {
        f.dodge = 19; f.invuln = 13; f.dodgeCooldown = 44; f.attack = null; f.vx = c.x * 13;
        if (!f.grounded) { f.vy = c.y * 8; f.airDodgeUsed = true; }
        state(f, 'dodge'); this.emit('dodge', f); locked = true;
      } else if (f.jumpBuffer > 0) {
        const p = this.stage.platforms.find(p => p.id === f.platformId);
        if (f.grounded && c.y < -0.5 && p && !p.solid) {
          f.dropFrames = 18; f.y -= 0.09; f.grounded = false; f.vy = -2; f.jumpBuffer = 0;
        } else if (f.grounded) {
          f.jumpSquat = 3; f.jumpBuffer = 0; state(f, 'jumpsquat'); locked = true;
        } else if (f.jumpsLeft > 0) {
          f.jumpsLeft--; f.vy = f.definition.jump * 0.96; f.fastFall = false; f.jumpBuffer = 0; this.emit('jump', f);
        }
      }
      if (!locked && f.queuedAttack) {
        const q = f.queuedAttack;
        this.beginAttack(f, this.chooseAttack(f, q.type, q)); f.queuedAttack = null; locked = Boolean(f.attack);
      }
      if (!locked && c.shield && f.grounded && f.shield > 0) {
        state(f, 'shield'); f.shield = Math.max(0, f.shield - 0.36); f.vx = approach(f.vx, 0, 0.9); locked = true;
        if (f.shield === 0) this.breakShield(f);
      }
    }
    if (f.state !== 'shield') f.shield = Math.min(100, f.shield + 0.22);
    if (f.stun === 0 && f.dodge === 0) {
      if (!locked || (!f.grounded && !f.charging)) {
        f.vx = approach(f.vx, c.x * f.definition.speed, f.grounded ? 0.95 : f.definition.airControl);
      } else if (f.grounded) f.vx = approach(f.vx, 0, 0.7);
    } else if (f.stun > 0) f.vx *= 0.988;
    if (c.y < -0.6 && f.vy < 0 && !f.grounded && f.stun === 0) { f.fastFall = true; f.vy = Math.min(f.vy, -26); }
    if (!c.jumpHeld && f.vy > f.definition.jump * 0.67 && !f.attack && f.stun === 0) f.vy = f.definition.jump * 0.67;
    const oldY = f.y;
    f.vy = Math.max(f.fastFall ? -26 : -20, f.vy - f.definition.gravity * DT);
    f.x += f.vx * DT; f.y += f.vy * DT;
    const landed = f.vy <= 0 ? landingPlatform(this.stage, oldY, f.y, f.x, f.dropFrames > 0) : null;
    const wasGrounded = f.grounded; f.grounded = Boolean(landed);
    if (landed) {
      f.y = landed.y; f.vy = 0; f.platformId = landed.id; f.jumpsLeft = 1;
      f.recoveryUsed = false; f.helpless = false; f.airDodgeUsed = false; f.fastFall = false;
      if (!wasGrounded) {
        if (f.attack) {
          const m = MOVES[f.attack]; f.landing = m.landingLag || (m.recoveryMove ? 18 : 0); f.attack = null; f.charging = false;
        }
        this.emit('land', f, { y: f.y });
      }
    } else {
      f.platformId = null; this.collideSolidSides(f);
      if (f.stun === 0 && f.dodge === 0 && !f.attack && f.ledgeCooldown === 0 && c.y > -0.5) this.tryLedge(f);
    }
    if (f.state !== 'ledge' && !f.attack && !f.stun && !f.dodge && !f.landing && !f.jumpSquat && !f.shieldStun && !(c.shield && f.grounded && locked)) {
      state(f, f.grounded ? (Math.abs(f.vx) > 0.4 ? 'run' : 'idle') : f.vy > 0 ? 'jump' : 'fall');
    }
  }
  collideSolidSides(f) {
    for (const p of this.stage.platforms) {
      if (!p.solid || f.y >= p.y - 0.03 || f.y + 1.9 <= p.y - 0.95) continue;
      const left = p.x - p.width / 2, right = p.x + p.width / 2;
      if (f.x > left - 0.42 && f.x < right + 0.42) {
        if (f.prevX <= left) { f.x = left - 0.44; f.vx = Math.min(0, f.vx); }
        else if (f.prevX >= right) { f.x = right + 0.44; f.vx = Math.max(0, f.vx); }
        else if (f.prevY + 1.9 <= p.y - 0.95) { f.y = p.y - 2.87; f.vy = Math.min(0, f.vy); }
      }
    }
  }
  tryLedge(f) {
    if (f.vy > 0 || f.dropFrames > 0) return;
    const p = this.stage.platforms.find(p => p.solid);
    if (!p || f.y < p.y - 1.8 || f.y > p.y - 0.05) return;
    for (const side of [-1, 1]) {
      const x = p.x + p.width / 2 * side;
      if (Math.abs(f.x - x) > 0.75) continue;
      if (this.fighters.some(other => other.id !== f.id && other.state === 'ledge' && other.ledgeSide === side)) continue;
      f.x = x + side * 0.45; f.y = p.y - 1.1; f.vx = 0; f.vy = 0; f.ledgeSide = side;
      f.invuln = 32; f.recoveryUsed = false; f.helpless = false; f.jumpsLeft = 1; state(f, 'ledge'); this.emit('ledge', f);
    }
  }
  handleLedge(f, c) {
    const side = f.ledgeSide;
    if (f.jumpBuffer > 0) {
      f.vy = f.definition.jump; f.vx = -side * 5; f.jumpsLeft = 1; f.jumpBuffer = 0; f.ledgeCooldown = 45; state(f, 'jump');
    } else if (c.y < -0.5 || c.x * side > 0.5 || f.stateFrame > 100) {
      f.ledgeCooldown = 45; f.vy = -2; state(f, 'fall');
    } else if (c.x * side < -0.4 || c.attack || c.shield) {
      const p = this.stage.platforms.find(p => p.solid);
      f.x = p.x + (p.width / 2 - 0.85) * side; f.y = p.y; f.grounded = true; f.platformId = p.id;
      f.facing = -side; f.landing = 10; f.ledgeCooldown = 45; state(f, 'landing');
    }
  }
  breakShield(f) { f.shield = 20; f.shieldStun = 0; f.stun = 95; f.vy = 7; f.grounded = false; state(f, 'hurt'); this.emit('shieldbreak', f); }
  resolveHits(controls) {
    // Gather before resolving so simultaneous attacks can trade without player-order bias.
    const hits = [];
    for (const attacker of this.fighters) {
      if (attacker.hitstop > 0 || !attacker.attack) continue;
      const circles = hitboxes(attacker); if (!circles.length) continue;
      for (const target of this.fighters) {
        if (target.id === attacker.id || target.ko || !target.stocks || target.invuln || attacker.hitTargets.includes(target.id)) continue;
        if (circles.some(h => hurtboxes(target).some(body => circleOverlap(h, body)))) {
          hits.push({ attacker, target, move: MOVES[attacker.attack], charge: attacker.chargeFrames, facing: attacker.facing, shielded: target.state === 'shield' && target.grounded });
        }
      }
    }
    for (const { attacker, target, move, charge, facing, shielded } of hits) {
      attacker.hitTargets.push(target.id);
      if (shielded) {
        target.shield = Math.max(0, target.shield - move.damage * 1.8 - 5);
        target.shieldStun = Math.ceil(move.damage * 0.75) + 4; target.vx = facing * 2.5;
        attacker.hitstop = target.hitstop = 4; this.emit('block', target);
        if (!target.shield) this.breakShield(target); continue;
      }
      const damage = move.damage * attacker.definition.power * (1 + charge / 45 * 0.45);
      target.damage = Math.min(999, target.damage + damage);
      const force = knockback(move, target.damage, target.definition.weight, charge, attacker.definition.power);
      const direction = move.bilateral ? sign(target.x - attacker.x) : facing * (move.reverse ? -1 : 1);
      const angle = radians(move.angle);
      let vx = Math.cos(angle) * direction, vy = Math.sin(angle);
      const di = controls[target.id] || EMPTY_INPUT;
      const rotate = clamp(vx * di.y - vy * di.x, -1, 1) * radians(18);
      const nextX = vx * Math.cos(rotate) - vy * Math.sin(rotate);
      vy = vx * Math.sin(rotate) + vy * Math.cos(rotate); vx = nextX;
      target.vx = vx * force; target.vy = vy * force;
      if (target.grounded && target.vy > 0) target.y += 0.04;
      target.grounded = false; target.stun = clamp(Math.ceil(force * 2), 9, 75);
      target.attack = null; target.charging = false; target.jumpSquat = 0; target.landing = 0;
      target.dodge = 0; target.helpless = false; target.fastFall = false;
      attacker.hitstop = target.hitstop = clamp(Math.floor(damage * 0.32 + 3), 4, 11);
      state(target, 'hurt'); this.totalHits++;
      this.emit('hit', target, { damage, force, attacker: attacker.id, color: attacker.definition.color });
    }
  }
  ringOut(f) {
    this.emit('ko', f);
    if (this.settings.mode !== 'training') f.stocks--;
    f.ko = 80; f.attack = null; f.charging = false; f.hitstop = 0;
    f.vx = f.vy = 0; state(f, 'ko');
  }
  respawn(f) {
    const stocks = f.stocks;
    Object.assign(f, createFighter(f.id, f.definition, stocks));
    f.x = f.prevX = f.id === 0 ? -2 : 2; f.y = f.prevY = 8.6; f.grounded = false;
    f.invuln = 120; state(f, 'fall');
    if (this.settings.mode === 'training' && f.id === 1) f.damage = this.settings.trainingDamage;
    this.emit('respawn', f);
  }
}
